import { dataProvider } from "@/@core/http";
import { PAYMENT_PLANS, SUBSCRIPTION_STATUS } from "@/constant";

const getFormattedPlanLimits = subscription => {
  const { domains, users: teammates } = subscription.features;

  let formattedDomainsLimit = "";
  let formattedTeammatesLimit = "";

  if (domains) {
    formattedDomainsLimit = domains.toString();
  } else {
    formattedDomainsLimit = "Unlimited";
  }

  if (teammates) {
    formattedTeammatesLimit = teammates.toString();
  } else {
    formattedTeammatesLimit = "Unlimited";
  }

  // There is specific case with Agency plan that "under the hood" domains are limited to 50. We still have to display "Unlimited".
  if (subscription.plan === PAYMENT_PLANS.AGENCY.name.toLowerCase()) {
    formattedDomainsLimit = "Unlimited";
    formattedTeammatesLimit = "Unlimited";
  }

  return {
    formattedDomainsLimit,
    formattedTeammatesLimit,
  };
};

export default {
  namespaced: true,
  state: {
    // Stripe object.
    stripe: null,
    // Subscription data that is set after the user logs in. Updated with more data after getSubscriptionData()
    subscription: null,
    subscriptionDataLoading: false,
    // Company details
    companyDetails: null,
    companyDetailsLoading: false,
    // Payment methods
    paymentMethods: [],
    paymentMethodsLoading: false,
    // Billing history (invoices)
    invoices: [],
    invoicesLoading: false,
  },
  getters: {
    getTrialEndsAt(state) {
      return state.subscription?.trialEndsAt;
    },
    getIsTrial(state) {
      return state.subscription?.status === SUBSCRIPTION_STATUS.TRIALING;
    },
    getSubscription(state) {
      return state.subscription;
    },
    getSubscriptionDataLoading(state) {
      return state.subscriptionDataLoading;
    },
    displayRetryPaymentBtn(state) {
      if (state.subscriptionDataLoading) return false;

      return !!state.subscription?.retryPaymentAllowed;
    },
    getCompanyDetails(state) {
      return state.companyDetails;
    },
    getCompanyDetailsLoading(state) {
      return state.companyDetailsLoading;
    },
    getPaymentMethods(state) {
      return state.paymentMethods;
    },
    getPaymentMethodsLoading(state) {
      return state.paymentMethodsLoading;
    },
    getInvoices(state) {
      return state.invoices;
    },
    getInvoicesLoading(state) {
      return state.invoicesLoading;
    },
  },
  actions: {
    async getStripe({ commit, state }) {
      if (process.env.VUE_APP_INULTI_BUILD === "true") {
        return undefined;
      }
      if (state.stripe) {
        return state.stripe;
      }

      const stripeLib = await import("@stripe/stripe-js");
      const stripe = await stripeLib.loadStripe(process.env.VUE_APP_STRIPE_KEY);
      commit("SET_STRIPE", stripe);

      return stripe;
    },
    async loadSubscriptionData({ rootState, commit, state }) {
      commit("SET_SUBSCRIPTION_DATA_LOADING", true);

      try {
        const organizationId = rootState.currentlyActiveOrganization?.organization?.id;
        const { data } = await dataProvider.customHttpCall("GET", `organizations/${organizationId}/subscription`);
        const limits = getFormattedPlanLimits(data);

        const subscriptionData = {
          // So that properties that were set when user logged in are remained
          ...state.subscription,
          ...data,
          ...limits,
        };

        commit("SET_SUBSCRIPTION", subscriptionData);

        return subscriptionData;
      } catch (e) {
        return Promise.reject(e);
      } finally {
        commit("SET_SUBSCRIPTION_DATA_LOADING", false);
      }
    },
    async getCompanyDetails({ state, commit }) {
      commit("SET_COMPANY_DETAILS_LOADING", true);

      try {
        const { data } = await dataProvider.customHttpCall(
          "GET",
          `subscriptions/${state.subscription.id}/billing_information`,
        );
        commit("SET_COMPANY_DETAILS", data);

        return data;
      } catch (e) {
        return Promise.reject(e);
      } finally {
        commit("SET_COMPANY_DETAILS_LOADING", false);
      }
    },
    async getPaymentMethods({ state, commit }) {
      commit("SET_PAYMENT_METHODS_LOADING", true);

      try {
        const { data } = await dataProvider.customHttpCall(
          "GET",
          `subscriptions/${state.subscription.id}/payment_methods`,
        );
        commit("SET_PAYMENT_METHODS", data);

        return data;
      } catch (e) {
        return Promise.reject(e);
      } finally {
        commit("SET_PAYMENT_METHODS_LOADING", false);
      }
    },
    async getInvoices({ state, commit }) {
      commit("SET_INVOICES_LOADING", true);

      try {
        const { data } = await dataProvider.customHttpCall("GET", `subscriptions/${state.subscription.id}/invoices`);
        commit("SET_INVOICES", data);

        return data;
      } catch (e) {
        return Promise.reject(e);
      } finally {
        commit("SET_INVOICES_LOADING", false);
      }
    },
  },
  mutations: {
    SET_STRIPE(state, value) {
      state.stripe = value;
    },
    SET_SUBSCRIPTION(state, value) {
      state.subscription = value;
    },
    SET_SUBSCRIPTION_DATA_LOADING(state, value) {
      state.subscriptionDataLoading = value;
    },
    SET_COMPANY_DETAILS(state, value) {
      state.companyDetails = value;
    },
    SET_COMPANY_DETAILS_LOADING(state, value) {
      state.companyDetailsLoading = value;
    },
    SET_PAYMENT_METHODS(state, value) {
      state.paymentMethods = value;
    },
    SET_PAYMENT_METHODS_LOADING(state, value) {
      state.paymentMethodsLoading = value;
    },
    SET_INVOICES(state, value) {
      state.invoices = value;
    },
    SET_INVOICES_LOADING(state, value) {
      state.invoicesLoading = value;
    },
  },
};
